// Find a Retailer store locator.
// Fetches the retailer JSON asset, renders a branded Leaflet map (clustered pins)
// beside a searchable, country-filterable card list. Leaflet + markercluster load
// from cdnjs on this template only; the list still works if that fails.
const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=cb1_3ln0_1_05d075c3ed09fb0ff09769f0';
//const TILE_URL = 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png';
const TILE_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';
const TILE_SUBDOMAINS = 'abcd';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
const safeUrl = (u) => (typeof u === 'string' && /^https?:\/\//i.test(u.trim())) ? u.trim() : '';

// Map a Storefront `metaobject` node -> the flat record the map/list/search use.
function recordFromNode(node) {
  const f = Object.create(null);
  for (const { key, value } of node.fields) if (value != null) f[key] = value;
  const lat = f.latitude !== undefined ? parseFloat(f.latitude) : NaN;
  const lng = f.longitude !== undefined ? parseFloat(f.longitude) : NaN;
  return {
    id: node.handle,
    name: f.name || '',
    address: f.address || '',
    city: f.city || '',
    region: f.region || '',
    country: f.country || '',
    postcode: f.postcode || '',
    phone: f.phone || '',
    website: f.website || '',
    lat: Number.isFinite(lat) ? lat : null,
    lng: Number.isFinite(lng) ? lng : null,
  };
}

// Resolve once Leaflet + markercluster are on window, or false after a timeout.
function whenLeafletReady(timeout = 10000) {
  return new Promise((resolve) => {
    const ready = () => window.L && typeof window.L.markerClusterGroup === 'function';
    if (ready()) return resolve(true);
    const start = Date.now();
    const iv = setInterval(() => {
      if (ready()) { clearInterval(iv); resolve(true); }
      else if (Date.now() - start > timeout) { clearInterval(iv); resolve(false); }
    }, 60);
  });
}

class IntuitionRetailerMap extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.mapEl = this.querySelector('[data-rl-map]');
    this.listEl = this.querySelector('[data-rl-list]');
    this.emptyEl = this.querySelector('[data-rl-empty]');
    this.searchEl = this.querySelector('[data-rl-search]');
    this.countryEl = this.querySelector('[data-rl-country]');
    this.countEl = this.querySelector('[data-rl-count]');
    this.resetEl = this.querySelector('[data-rl-reset]');
    this.url = this.dataset.retailersUrl;
    this.shop = this.dataset.shop;
    this.apiVersion = this.dataset.apiVersion || '2026-07';
    this.token = this.dataset.token;
    this.type = this.dataset.type || 'retailer';
    this.markerById = new Map();
    this.retailers = [];
    this.start();
  }

  async start() {
    const data = await this.loadRetailers();
    if (!Array.isArray(data)) { this.showLoadError(); return; }
    this.retailers = data;

    this.buildCountryOptions();
    this.wireControls();
    this.render(this.retailers);           // list renders immediately, no map dependency

    const ok = await whenLeafletReady();
    if (ok) { this.buildMap(); this.render(this.filtered()); this.fitTo(this.pinned(this.filtered()), false); }
    else { this.showMapFallback(); }
  }

  // Storefront API (retailer metaobjects) first; bundled JSON asset as offline fallback.
  async loadRetailers() {
    if (this.token && this.shop) {
      try {
        const rows = await this.fetchFromStorefront();
        if (rows.length) { console.info(`[retailer-map] ${rows.length} from Storefront API`); return rows; }
      } catch (e) { console.warn('[retailer-map] Storefront fetch failed, using JSON fallback', e); }
    }
    const rows = await this.fetchFromAsset();
    if (Array.isArray(rows)) console.info(`[retailer-map] ${rows.length} from bundled JSON (fallback)`);
    return rows;
  }

  async fetchFromStorefront() {
    const endpoint = `https://${this.shop}/api/${this.apiVersion}/graphql.json`;
    const query = `query($type:String!,$first:Int!,$after:String){
      metaobjects(type:$type, first:$first, after:$after){
        edges { node { handle fields { key value } } }
        pageInfo { hasNextPage endCursor }
      }
    }`;
    const out = [];
    let after = null;
    do {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': this.token },
        body: JSON.stringify({ query, variables: { type: this.type, first: 250, after } }),
      });
      if (!res.ok) throw new Error('storefront HTTP ' + res.status);
      const json = await res.json();
      if (json.errors) throw new Error('storefront GraphQL error');
      const mo = json.data && json.data.metaobjects;
      if (!mo) throw new Error('no metaobjects field');
      for (const edge of mo.edges) out.push(recordFromNode(edge.node));
      after = mo.pageInfo.hasNextPage ? mo.pageInfo.endCursor : null;
    } while (after);
    out.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'en'));
    return out;
  }

  async fetchFromAsset() {
    if (!this.url) return null;
    try {
      const res = await fetch(this.url, { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      const data = await res.json();
      return Array.isArray(data) ? data : null;
    } catch (e) { return null; }
  }

  // --------------------------------------------------------------- data
  pinned(list) { return list.filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lng)); }

  filtered() {
    const q = (this.searchEl.value || '').trim().toLowerCase();
    const country = this.countryEl.value || '';
    return this.retailers.filter(r => {
      if (country && r.country !== country) return false;
      if (!q) return true;
      const hay = [r.name, r.address, r.city, r.region, r.country, r.postcode].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }

  buildCountryOptions() {
    const counts = {};
    for (const r of this.retailers) if (r.country) counts[r.country] = (counts[r.country] || 0) + 1;
    const names = Object.keys(counts).sort((a, b) => a.localeCompare(b));
    const frag = document.createDocumentFragment();
    const all = document.createElement('option');
    all.value = '';
    all.textContent = `All countries (${this.retailers.length})`;
    frag.appendChild(all);
    for (const name of names) {
      const opt = document.createElement('option');
      opt.value = name;
      opt.textContent = `${name} (${counts[name]})`;
      frag.appendChild(opt);
    }
    this.countryEl.appendChild(frag);
  }

  // --------------------------------------------------------------- map
  buildMap() {
    const L = window.L;
    this.map = L.map(this.mapEl, { scrollWheelZoom: false, worldCopyJump: true, zoomControl: true });
    this.map.setView([30, -20], 2);
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, subdomains: TILE_SUBDOMAINS, maxZoom: 19 }).addTo(this.map);
    // Enable wheel zoom only after a click, so scrolling the page does not trap the pointer.
    this.map.on('focus', () => this.map.scrollWheelZoom.enable());
    this.map.on('blur', () => this.map.scrollWheelZoom.disable());

    const pinIcon = L.divIcon({ className: 'rl-pin-wrap', html: '<div class="rl-pin"></div>', iconSize: [22, 22], iconAnchor: [11, 22], popupAnchor: [0, -20] });

    this.cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 46,
      chunkedLoading: true,
    });

    for (const r of this.pinned(this.retailers)) {
      const marker = L.marker([r.lat, r.lng], { icon: pinIcon, title: r.name, keyboard: true, alt: r.name });
      marker.bindPopup(() => this.popupContent(r), { className: 'rl-popup', closeButton: true });
      marker.on('popupopen', () => this.highlightCard(r.id, true));
      marker.on('popupclose', () => this.highlightCard(r.id, false));
      this.markerById.set(r.id, marker);
    }
    this.map.addLayer(this.cluster);
    requestAnimationFrame(() => this.map.invalidateSize());
    window.addEventListener('resize', debounce(() => this.map && this.map.invalidateSize(), 200));
  }

  syncMarkers(list) {
    if (!this.cluster) return;
    this.cluster.clearLayers();
    const markers = [];
    for (const r of this.pinned(list)) {
      const m = this.markerById.get(r.id);
      if (m) markers.push(m);
    }
    this.cluster.addLayers(markers);
  }

  fitTo(list, animate = true) {
    if (!this.map || !list.length) return;
    const L = window.L;
    const bounds = L.latLngBounds(list.map(r => [r.lat, r.lng]));
    this.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13, animate: animate && !reduceMotion });
  }

  focusRetailer(r) {
    if (!this.map) return;
    const marker = this.markerById.get(r.id);
    if (!marker) return;
    const open = () => marker.openPopup();
    if (this.cluster && this.cluster.hasLayer(marker)) this.cluster.zoomToShowLayer(marker, open);
    else { this.map.setView([r.lat, r.lng], Math.max(this.map.getZoom(), 12), { animate: !reduceMotion }); open(); }
  }

  popupContent(r) {
    const el = document.createElement('div');
    const name = document.createElement('p'); name.className = 'rl-popup__name'; name.textContent = r.name; el.appendChild(name);
    if (r.address) { const a = document.createElement('p'); a.className = 'rl-popup__addr'; a.textContent = r.address; el.appendChild(a); }
    if (r.phone) { const p = document.createElement('p'); p.className = 'rl-popup__phone'; p.textContent = r.phone; el.appendChild(p); }
    const links = this.actionLinks(r, 'rl-popup__web', 'rl-popup__dir');
    if (links) { const wrap = document.createElement('div'); wrap.className = 'rl-popup__links'; wrap.appendChild(links); el.appendChild(wrap); }
    return el;
  }

  // --------------------------------------------------------------- list
  render(list) {
    // cards
    this.listEl.innerHTML = '';
    const frag = document.createDocumentFragment();
    for (const r of list) frag.appendChild(this.card(r));
    this.listEl.appendChild(frag);

    // count + empty state
    const total = this.retailers.length;
    this.countEl.textContent = list.length === total
      ? `${total} locations`
      : `Showing ${list.length} of ${total} locations`;
    const empty = list.length === 0;
    this.emptyEl.classList.toggle('is-visible', empty);
    this.listEl.style.display = empty ? 'none' : '';

    this.syncMarkers(list);
  }

  card(r) {
    const li = document.createElement('li');
    li.className = 'rl-card';
    li.dataset.id = r.id;
    const pinned = Number.isFinite(r.lat) && Number.isFinite(r.lng);

    const body = document.createElement('div');
    body.className = 'rl-card__body';

    // Name is a button for pinned retailers (focuses the map), plain text otherwise.
    // Address/phone/links stay OUTSIDE it so the tel: link is not nested in a button
    // and the address is exposed to screen readers as normal content.
    const name = document.createElement(pinned ? 'button' : 'p');
    name.className = 'rl-card__name';
    name.textContent = r.name;
    if (pinned) {
      name.type = 'button';
      name.setAttribute('aria-label', `Show ${r.name} on the map`);
      name.addEventListener('click', () => this.focusRetailer(r));
    }
    body.appendChild(name);

    if (r.address) { const a = document.createElement('p'); a.className = 'rl-card__addr'; a.textContent = r.address; body.appendChild(a); }
    else if (r.country) { const a = document.createElement('p'); a.className = 'rl-card__addr'; a.textContent = r.country; body.appendChild(a); }
    if (r.phone) {
      const p = document.createElement('p'); p.className = 'rl-card__phone';
      const tel = document.createElement('a'); tel.href = 'tel:' + r.phone.replace(/[^\d+]/g, ''); tel.textContent = r.phone;
      p.appendChild(tel); body.appendChild(p);
    }
    li.appendChild(body);

    const actions = this.actionLinks(r, 'rl-link rl-link--web', 'rl-link rl-link--dir');
    if (actions) { const wrap = document.createElement('div'); wrap.className = 'rl-card__actions'; wrap.appendChild(actions); li.appendChild(wrap); }

    // Whole-card click (mouse) focuses the map, unless a link/button was the target.
    if (pinned) {
      li.classList.add('is-clickable');
      li.addEventListener('click', (e) => { if (!e.target.closest('a, button')) this.focusRetailer(r); });
    }
    return li;
  }

  // Website + Directions links shared by cards and popups. Returns a fragment or null.
  actionLinks(r, webClass, dirClass) {
    const frag = document.createDocumentFragment();
    let any = false;
    const dir = this.directionsUrl(r);
    if (dir) {
      const a = document.createElement('a');
      a.className = dirClass; a.href = dir; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = 'Directions'; frag.appendChild(a); any = true;
    }
    const web = safeUrl(r.website);
    if (web) {
      const a = document.createElement('a');
      a.className = webClass; a.href = web; a.target = '_blank'; a.rel = 'noopener';
      a.textContent = 'Website'; frag.appendChild(a); any = true;
    }
    return any ? frag : null;
  }

  directionsUrl(r) {
    if (Number.isFinite(r.lat) && Number.isFinite(r.lng)) {
      return `https://www.google.com/maps/dir/?api=1&destination=${r.lat},${r.lng}`;
    }
    const q = [r.name, r.address, r.country].filter(Boolean).join(' ');
    return q ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}` : '';
  }

  highlightCard(id, on) {
    this.listEl.querySelectorAll('.rl-card.is-active').forEach(el => el.classList.remove('is-active'));
    if (!on) return;
    const card = this.listEl.querySelector(`.rl-card[data-id="${CSS.escape(String(id))}"]`);
    if (!card) return;
    card.classList.add('is-active');
    this.scrollCardIntoList(card);
  }

  // Scroll a card into view WITHIN the list container only, never the page — so
  // opening a popup from a map pin does not yank the mobile viewport off the map.
  scrollCardIntoList(card) {
    const list = this.listEl;
    const oy = getComputedStyle(list).overflowY;
    if (oy !== 'auto' && oy !== 'scroll') return;
    const cr = card.getBoundingClientRect();
    const lr = list.getBoundingClientRect();
    if (cr.top < lr.top) list.scrollTop += cr.top - lr.top - 8;
    else if (cr.bottom > lr.bottom) list.scrollTop += cr.bottom - lr.bottom + 8;
  }

  // --------------------------------------------------------------- events
  wireControls() {
    const apply = () => {
      const list = this.filtered();
      this.render(list);
      this.fitTo(this.pinned(list));
    };
    this.searchEl.addEventListener('input', debounce(apply, 130));
    this.countryEl.addEventListener('change', apply);
    if (this.resetEl) this.resetEl.addEventListener('click', () => {
      this.searchEl.value = ''; this.countryEl.value = '';
      this.render(this.retailers);
      this.fitTo(this.pinned(this.retailers));
      this.searchEl.focus();
    });
  }

  // --------------------------------------------------------------- errors
  showMapFallback() {
    this.mapEl.innerHTML = '<div class="rl-map-fallback">The map could not load. Browse the full list of retailers below.</div>';
  }
  showLoadError() {
    if (this.mapEl) this.mapEl.innerHTML = '<div class="rl-map-fallback">Retailer list is temporarily unavailable. Please try again shortly.</div>';
    if (this.countEl) this.countEl.textContent = '';
  }
}

if (!customElements.get('intuition-retailer-map')) {
  customElements.define('intuition-retailer-map', IntuitionRetailerMap);
}
